import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import {
  DndContext,
  DragOverlay,
  type DropAnimation,
  defaultDropAnimationSideEffects,
  MeasuringStrategy,
  MouseSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
} from "@dnd-kit/sortable";
import { useMutation, useQuery } from "@tanstack/react-query";

// Avoids TS2589 (type instantiation is excessively deep) from api.d.ts chain
export interface LocationItem {
  id: Id<"locations">;
  name: string;
  logoUrl?: string;
  phoneNumber?: string;
  addressLine1?: string;
  addressLine2?: string | null;
  city?: string;
  state?: string;
  zipCode?: string;
  style: "QSR" | "FSR";
  operationalStatus: string;
  posStatus: "inactive" | "fully-online" | "partially-online" | "offline";
  sortOrder: number;
  createdAt: number;
}

const locationsGetAllMineRef = api.locations.getAllMine;

const updateSortOrderRef = api.locations.updateSortOrder;

import { convexQuery } from "@convex-dev/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useDebounce } from "use-debounce";

import { convexClient } from "~/integrations/convex/provider";
import { ProductEvents } from "~/lib/posthog";
import { closestCenter } from "~/utils/dnd";

import { LocationCard } from "./location-card";
import { LocationsListFilter } from "./locations-list-filter";
import type { SearchOptions } from "./locations-types";

const dropAnimation: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: "0.5",
      },
    },
  }),
};

export function LocationsList() {
  const [locationsList, setLocationsList] = useState<LocationItem[]>([]);
  const [activeItem, setActiveItem] = useState<LocationItem | null>(null);

  const { data: locationsQuery } = useQuery(
    convexQuery(locationsGetAllMineRef, {})
  );
  const locations = useMemo(() => locationsQuery ?? [], [locationsQuery]);

  const updateSortOrderMutation = useMutation({
    mutationFn: (data: {
      updates: { id: Id<"locations">; sortOrder: number }[];
    }) => convexClient.mutation(updateSortOrderRef, data),
    onSuccess: () => {
      ProductEvents.featureUsed("location_reorder");
    },
  });

  const [filterQuery, setFilterQuery] = useState("");
  const [filterQueryValue] = useDebounce(filterQuery, 500);

  const [filterSortBy, setFilterSortBy] =
    useState<SearchOptions["sortBy"]>("custom");
  const [filterStyle, setFilterStyle] = useState<
    SearchOptions["filters"]["style"]
  >([]);
  const [filterOperationalStatus, setFilterOperationalStatus] = useState<
    SearchOptions["filters"]["operationalStatus"]
  >([]);

  const filteredAndSortedLocations = useMemo(() => {
    if (!locations) {
      return [];
    }

    let result = locations;

    if (filterQueryValue) {
      result = result.filter((location) =>
        location.name.toLowerCase().includes(filterQueryValue.toLowerCase())
      );
    }

    if (filterStyle.length > 0) {
      result = result.filter((location) =>
        filterStyle.includes(location.style)
      );
    }

    if (filterOperationalStatus.length > 0) {
      result = result.filter((location) =>
        filterOperationalStatus.includes(location.operationalStatus)
      );
    }

    switch (filterSortBy) {
      case "name-asc":
        result = result
          .slice()
          .toSorted((a, b) => a.name.localeCompare(b.name));
        break;
      case "name-desc":
        result = result
          .slice()
          .toSorted((a, b) => b.name.localeCompare(a.name));
        break;
      case "createdAt":
        result = result.slice().toSorted((a, b) => a.createdAt - b.createdAt);
        break;
      default:
        // "custom" sort order - use existing order from database
        break;
    }

    return result;
  }, [
    locations,
    filterQueryValue,
    filterSortBy,
    filterStyle,
    filterOperationalStatus,
  ]);

  // Keep local state for drag and drop
  useEffect(() => {
    setLocationsList(filteredAndSortedLocations);
  }, [filteredAndSortedLocations]);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { delay: 250, tolerance: 0 },
    })
  );

  const getLocationPosition = useCallback(
    (id: string) => locationsList.findIndex((r) => r.id === id),
    [locationsList]
  );

  return (
    <div className="space-y-4">
      <LocationsListFilter
        filterOperationalStatus={filterOperationalStatus}
        filterQuery={filterQuery}
        filterSortBy={filterSortBy}
        filterStyle={filterStyle}
        setFilterOperationalStatus={setFilterOperationalStatus}
        setFilterQuery={setFilterQuery}
        setFilterSortBy={setFilterSortBy}
        setFilterStyle={setFilterStyle}
      />
      <div
        className="grid grid-cols-[repeat(auto-fill,minmax(16rem,1fr))] gap-4"
        id="locationsContent"
      >
        <DndContext
          collisionDetection={closestCenter}
          measuring={{
            droppable: {
              strategy: MeasuringStrategy.Always,
            },
          }}
          onDragCancel={() => setActiveItem(null)}
          onDragEnd={({ active, over }) => {
            setActiveItem(null);
            if (active.id === over?.id) {
              return;
            }
            if (!over) {
              return;
            }
            const originalPosition = getLocationPosition(String(active.id));
            const newPosition = getLocationPosition(String(over.id));

            const movedList = arrayMove(
              locationsList,
              originalPosition,
              newPosition
            );
            const list: LocationItem[] = [];
            for (let i = 0; i < movedList.length; i++) {
              const item = movedList[i];
              if (item?.id === undefined) {
                continue;
              }
              list.push({
                ...item,
                id: item.id,
                sortOrder: i,
              });
            }

            setLocationsList(list);
            setFilterSortBy("custom");
            updateSortOrderMutation.mutate({
              updates: list.map((r) => ({
                id: r.id,
                sortOrder: r.sortOrder,
              })),
            });
          }}
          onDragStart={({ active }) => {
            const draggedItem = locationsList.find((r) => r.id === active.id);

            if (!draggedItem) {
              return;
            }

            setActiveItem(draggedItem);
          }}
          sensors={sensors}
        >
          <SortableContext items={locations} strategy={rectSortingStrategy}>
            {locationsList?.map((location) => (
              <LocationCard
                key={location.id}
                location={location}
                opacity={
                  activeItem?.id === location.id ? "opacity-50" : undefined
                }
              />
            ))}
          </SortableContext>
          {activeItem
            ? createPortal(
                <DragOverlay adjustScale={false} dropAnimation={dropAnimation}>
                  <LocationCard active={!!activeItem} location={activeItem} />
                </DragOverlay>,
                document.body
              )
            : null}
        </DndContext>
      </div>
    </div>
  );
}
